import { Caption1, Title3, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircleRegular, ClockRegular, GlobeRegular, WarningRegular } from '@fluentui/react-icons';

import { TChangePlan } from '../../models';
import { getAttentionSummary } from '../../utils';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  tile: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: '150px',
    flex: '1 1 150px',
  },
  icon: { fontSize: '24px', color: tokens.colorNeutralForeground3, display: 'flex' },
  attentionIcon: { color: tokens.colorStatusDangerForeground1 },
  warnIcon: { color: tokens.colorStatusWarningForeground1 },
  okIcon: { color: tokens.colorStatusSuccessForeground1 },
  text: { display: 'flex', flexDirection: 'column' },
  label: { color: tokens.colorNeutralForeground3 },
});

// No more than four compact indicators (prompt Part 8). Zero-value indicators are
// hidden; when nothing needs attention a single calm "On track" tile is shown.
export const AttentionSummary = ({ plan }: { plan: TChangePlan }) => {
  const classes = useStyles();
  const summary = getAttentionSummary(plan);

  const indicators = [
    { key: 'overdue', label: 'Overdue', value: summary.overdue, icon: <WarningRegular />, tone: classes.attentionIcon },
    { key: 'dueSoon', label: 'Due soon', value: summary.dueSoon, icon: <ClockRegular />, tone: classes.warnIcon },
    {
      key: 'external',
      label: 'Awaiting external decision',
      value: summary.awaitingExternal,
      icon: <GlobeRegular />,
      tone: classes.warnIcon,
    },
  ].filter((indicator) => indicator.value > 0);

  if (indicators.length === 0) {
    return (
      <div className={classes.row}>
        <div className={classes.tile}>
          <span className={`${classes.icon} ${classes.okIcon}`}>
            <CheckmarkCircleRegular />
          </span>
          <div className={classes.text}>
            <Title3>On track</Title3>
            <Caption1 className={classes.label}>Nothing needs attention right now</Caption1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.row}>
      {indicators.map((indicator) => (
        <div key={indicator.key} className={classes.tile}>
          <span className={`${classes.icon} ${indicator.tone}`}>{indicator.icon}</span>
          <div className={classes.text}>
            <Title3>{indicator.value}</Title3>
            <Caption1 className={classes.label}>{indicator.label}</Caption1>
          </div>
        </div>
      ))}
    </div>
  );
};
